import React, { useState } from "react";
import { cn } from "@/lib/utils";

interface ChipProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  onRemove?: () => void;
  icon?: string;
  isEditing?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  hasValue?: boolean;
  "data-filter-chip"?: boolean;
}

export function Chip({
  children,
  className,
  onClick,
  onRemove,
  icon,
  isEditing = false,
  isLoading = false,
  hasError = false,
  hasValue = true,
  ...rest
}: ChipProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 h-8 px-3 rounded-md",
        "text-xs font-medium transition-all duration-200",
        "border cursor-pointer select-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
        hasError
          ? "bg-red-50 text-red-700 border-red-300"
          : isEditing
            ? "bg-blue-500 text-white border-blue-500 hover:bg-blue-600 active:bg-blue-700"
            : hasValue
              ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
              : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300",
        isLoading && "opacity-60 cursor-wait",
        className
      )}
      {...rest}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {isLoading ? (
        <i className="fa-solid fa-spinner fa-spin w-3.5 h-3.5" />
      ) : icon ? (
        <i className={cn(icon, "w-3.5 h-3.5 text-xs")} />
      ) : null}

      {hasError && <i className="fa-solid fa-exclamation-circle w-3.5 h-3.5" />}

      <span className="flex items-center gap-1">{children}</span>

      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={cn(
            "ml-0.5 -mr-1.5 p-1 rounded-sm transition-opacity",
            isEditing
              ? "hover:bg-white/20"
              : hasValue
                ? "hover:bg-blue-200/50"
                : "hover:bg-neutral-200/50",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500",
            isHovered ? "opacity-100" : "opacity-0"
          )}
          aria-label="Remove filter"
          type="button"
        >
          <i className="fa-solid fa-xmark text-xs" />
        </button>
      )}
    </div>
  );
}
